import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './NotificationsSection.scss';
import UIToast from '@/components/shared/UIToast';
import { useNotifications } from '@/hooks/useNotifications';
import { Notification } from '@/types/notifications';
import { profileSelector } from '@/store/profile/profileSlices';
import UISimpleTable, {
  SimpleTableColumn,
} from '@/components/shared/UISimpleTable';
import UIBadge, { BadgeVariant } from '@/components/shared/UIBadge';
import UIProperty from '@/components/shared/UIProperty';
import UIIconButton from '@/components/shared/UIIconButton';
import { safeToLowerCase } from '@/utils/stringUtils';

// The component's internal display-oriented data structure
interface DisplayNotification {
  id: string;
  date: string;
  isRead: boolean; // Corrected: using isRead directly
  subject: string;
  unit: string;
  property: string;
  propertyType: string;
}

interface NotificationsSectionProps {}

const getCategoryBadgeVariant = (category: string): BadgeVariant => {
  switch (safeToLowerCase(category)) {
    case 'payment':
      return 'primary';
    case 'ticket':
      return 'warning';
    case 'task':
      return 'info';
    case 'lease':
      return 'success';
    default:
      return 'secondary';
  }
};

const NotificationsSection: React.FC<NotificationsSectionProps> = () => {
  const { loading, error, fetchNotifications } = useNotifications();
  const [displayNotifications, setDisplayNotifications] = useState<
    DisplayNotification[]
  >([]);
  const [toastMessage, setToastMessage] = useState('');
  const navigate = useNavigate();
  const profile = useSelector(profileSelector);

  useEffect(() => {
    const loadData = async () => {
      if (!profile?.id) return;

      const filters = { limit: 7, tenantId: profile.id };
      const response = await fetchNotifications(filters);

      if (response && response.notifications) {
        const formatted: DisplayNotification[] = response.notifications.map(
          (n: Notification) => {
            let unit = '';
            let property = '';
            let propertyType = '';

            if (n.lease) {
              unit = n.lease.unit?.number || '';
              property = n.lease.unit?.property?.name || '';
              propertyType = n.lease.unit?.property?.type
                ? String(n.lease.unit.property.type)
                : '';
            }

            return {
              id: String(n.id),
              date: new Date(n.createdAt).toLocaleDateString('en-CA'),
              isRead: n.isRead, // Corrected: passing isRead directly
              subject: n.title,
              unit,
              property,
              propertyType,
              category: n.entityType,
            };
          },
        );
        setDisplayNotifications(formatted);
      }
    };

    loadData();
  }, [fetchNotifications, profile?.id]);

  useEffect(() => {
    if (error) {
      setToastMessage(error);
    }
  }, [error]);

  const handleViewAllClick = () => {
    navigate('/notifications');
  };

  const handleContactClick = () => {
    navigate('/chats');
  };

  const columns: SimpleTableColumn[] = [
    {
      key: 'date',
      label: 'DATE',
      render: (value: string) => <span>{value}</span>,
    },
    {
      key: 'category',
      label: 'CATEGORY',
      render: (value: string) => (
        <UIBadge variant={getCategoryBadgeVariant(value)} shape="pill">
          {value
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (l: string) => l.toUpperCase())}
        </UIBadge>
      ),
    },
    {
      key: 'isRead', // Corrected key
      label: 'STATUS',
      render: (
        isRead: boolean, // Corrected render logic
      ) =>
        isRead ? (
          <UIBadge variant="success" shape="rounded">
            READ
          </UIBadge>
        ) : (
          <UIBadge variant="primary" shape="rounded">
            UNREAD
          </UIBadge>
        ),
    },
    {
      key: 'subject',
      label: 'SUBJECT',
      render: (value: string) => <span>{value}</span>,
    },
    {
      key: 'unit',
      label: 'UNIT',
      render: (value: string) => <span>{value}</span>,
    },
    {
      key: 'property',
      label: 'PROPERTY',
      render: (value: string, row: DisplayNotification) => {
        return row.property ? (
          <UIProperty name={row.property} type={row.propertyType as 'residential' | 'commercial' | 'mixed'} />
        ) : null;
      },
    },
    {
      key: 'contact',
      label: '',
      render: (value: any, row: DisplayNotification) => (
        <UIIconButton
          iconType="contact"
          variant="success" // Corrected: green button
          size="medium"
          onClick={handleContactClick}
          aria-label={`Contact regarding ${row.subject}`}
        />
      ),
    },
  ];

  return (
    <div className="NotificationsSection">
      {toastMessage && (
        <UIToast
          message={toastMessage}
          type="error"
          onClose={() => setToastMessage('')}
        />
      )}
      <div className="NotificationsSection__title">
        <div className="NotificationsSection__content">
          <h4 className="NotificationsSection__heading">Notifications</h4>
          <button
            className="NotificationsSection__view-all-button"
            onClick={handleViewAllClick}
            type="button"
          >
            <span className="NotificationsSection__button-text">View all</span>
          </button>
        </div>
        <div className="NotificationsSection__line"></div>
      </div>

      {loading ? (
        <div className="NotificationsSection__loading">
          Loading notifications...
        </div>
      ) : (
        <UISimpleTable
          data={displayNotifications}
          columns={columns}
          getRowKey={(row: DisplayNotification) => row.id}
          className="NotificationsSection__table"
        />
      )}
    </div>
  );
};

export default NotificationsSection;
